import axios from 'axios';
import React from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import { REMARK_MAX_LENGTH } from '../../resources/constants';
import '../../resources/css/order.css';
import { RouterProps } from '../../types/generalTypes';
import {
    ORDER_INIT_ERRORS,
    PaymentError,
    PaymentErrorResponse,
} from '../../types/paymentErrors';
import Button from '../common/button';
import Container from '../common/container';
import LoadingCircle from '../common/loadingCircle';
import Snackbar from '../common/snackbar';
import OrderApi from './api/order_api';

const ORDER_INIT_STORAGE_KEY = 'orderInitPayload';
const ORDER_INIT_STORAGE_TTL_MS = 15 * 60 * 1000;

const storeOrderInitPayload = (payload: any) => {
    if (typeof sessionStorage === 'undefined') return;
    try {
        sessionStorage.setItem(
            ORDER_INIT_STORAGE_KEY,
            JSON.stringify({ ...payload, createdAt: Date.now() }),
        );
    } catch (error) {
        // ignore storage errors (quota, disabled, etc.)
    }
};

const readOrderInitPayload = () => {
    if (typeof sessionStorage === 'undefined') return null;
    try {
        const raw = sessionStorage.getItem(ORDER_INIT_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed?.request || !parsed?.email) return null;
        if (parsed?.createdAt && Date.now() - parsed.createdAt > ORDER_INIT_STORAGE_TTL_MS) {
            sessionStorage.removeItem(ORDER_INIT_STORAGE_KEY);
            return null;
        }
        return parsed;
    } catch (error) {
        return null;
    }
};

interface IProps {}

type Props = IProps & RouterProps & WrappedComponentProps;

interface State {
    /** Erreur affichée en corps de page (bloquante) */
    error: boolean;
    /** Message principal d'erreur */
    errorMessage: string;
    /** Message secondaire (détail Saferpay, etc.) */
    errorDetail: string;
    /** Snackbar visible */
    snackbarOpen: boolean;
    /** Le bouton "réessayer" est proposé */
    canRetry: boolean;
    /** Le bouton "retour au panier" est proposé */
    showBackToCart: boolean;
}

class OrderInit extends React.Component<Props, State> {
    private mounted = false;

    /** Arguments mémorisés pour pouvoir relancer le paiement (retry) */
    private paymentArgs: any[] = [];

    constructor(props: Props) {
        super(props);
        this.state = {
            error: false,
            errorMessage: '',
            errorDetail: '',
            snackbarOpen: false,
            canRetry: false,
            showBackToCart: false,
        };
    }

    componentDidMount() {
        this.mounted = true;
        this.bootstrapPayment();
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    // ------------------------------------------------------------------
    // Gestion des erreurs de paiement structurées
    // ------------------------------------------------------------------

    handlePaymentError = (data: PaymentErrorResponse) => {
        const { intl } = this.props;

        switch (data.codeError) {
            case ORDER_INIT_ERRORS.SERIALIZER_ERROR:
                // p_oin_100 - champs invalides
                this.setState({
                    error: true,
                    errorMessage: intl.formatMessage({ id: 'order.error.invalidFields' }),
                    snackbarOpen: true,
                    showBackToCart: true,
                });
                break;

            case ORDER_INIT_ERRORS.INVALID_AMOUNT:
                // p_oin_101 - montant nul ou invalide
                this.setState({
                    error: true,
                    errorMessage: intl.formatMessage({ id: 'order.error.invalidAmount' }),
                    snackbarOpen: true,
                    showBackToCart: true,
                });
                break;

            case ORDER_INIT_ERRORS.SAFERPAY_ERROR:
                // p_oin_102 - erreur métier Saferpay
                this.handleSaferpayError(data);
                break;

            case ORDER_INIT_ERRORS.UNEXPECTED_ERROR:
            default:
                // p_oin_103 - erreur inattendue
                this.setState({
                    error: true,
                    errorMessage: intl.formatMessage({ id: 'order.error.unexpected' }),
                    snackbarOpen: true,
                    showBackToCart: true,
                });
                break;
        }
    };

    handleSaferpayError = (data: PaymentErrorResponse) => {
        const { intl } = this.props;
        const behavior = data.saferpay?.behavior ?? 'DO_NOT_RETRY';
        const detail = data.saferpay?.errorMessage
            || data.saferpay?.errorDetail?.join(' ')
            || '';

        switch (behavior) {
            case 'RETRY':
                this.setState({
                    error: true,
                    errorMessage: intl.formatMessage({ id: 'order.error.saferpay.retry' }),
                    errorDetail: detail,
                    snackbarOpen: true,
                    canRetry: true,
                });
                break;

            case 'RETRY_LATER':
                this.setState({
                    error: true,
                    errorMessage: intl.formatMessage({ id: 'order.error.saferpay.retryLater' }),
                    errorDetail: detail,
                    snackbarOpen: true,
                    showBackToCart: true,
                });
                break;

            case 'OTHER_MEANS':
                this.setState({
                    error: true,
                    errorMessage: intl.formatMessage({ id: 'order.error.saferpay.otherMeans' }),
                    errorDetail: detail,
                    snackbarOpen: true,
                    showBackToCart: true,
                });
                break;

            case 'DO_NOT_RETRY':
            default:
                this.setState({
                    error: true,
                    errorMessage: intl.formatMessage({ id: 'order.error.saferpay.doNotRetry' }),
                    errorDetail: detail,
                    snackbarOpen: true,
                    showBackToCart: true,
                });
                break;
        }
    };

    // ------------------------------------------------------------------
    // Appel API
    // ------------------------------------------------------------------

    async getPaymentUrl(
        email: any,
        bookings: any,
        schoolId: any,
        departmentId: any,
        client: any,
        points: any,
        codeDiscountCode: any,
        codeDiscountAmount: any,
        couponCode: any,
        couponAmount: any,
    ) {
        const { match } = this.props;

        // Mémoriser les arguments pour un éventuel retry
        this.paymentArgs = [
            email,
            bookings,
            schoolId,
            departmentId,
            client,
            points,
            codeDiscountCode,
            codeDiscountAmount,
            couponCode,
            couponAmount,
        ];

        try {
            const url = await OrderApi.initializePayment(
                email,
                bookings,
                client,
                match.params.lang ? match.params.lang : 'fr',
                schoolId,
                departmentId,
                points,
                codeDiscountCode,
                codeDiscountAmount,
                couponCode,
                couponAmount,
            );

            // Succès : redirection vers Saferpay
            if (this.mounted) {
                window.location.href = url;
            }
        } catch (error) {
            if (!this.mounted) return;

            // Requête annulée (navigation utilisateur)
            if (axios.isCancel(error) || (error as any)?.code === 'ECONNABORTED') {
                return;
            }

            // Erreur structurée provenant du backend
            if (error instanceof PaymentError) {
                this.handlePaymentError(error.data);
                return;
            }

            // Erreur réseau ou non prévue → message générique
            const { intl } = this.props;
            this.setState({
                error: true,
                errorMessage: intl.formatMessage({ id: 'order.error.unexpected' }),
                snackbarOpen: true,
                showBackToCart: true,
            });
        }
    }

    // ------------------------------------------------------------------
    // Chargement du payload (state / sessionStorage)
    // ------------------------------------------------------------------

    private handleMissingPayload = () => {
        const { intl } = this.props;
        this.setState({
            error: true,
            errorMessage: intl.formatMessage({ id: 'order.error.sessionExpired' }),
            errorDetail: '',
            snackbarOpen: true,
            canRetry: false,
            showBackToCart: true,
        });
    };

    private bootstrapPayment = () => {
        const { history } = this.props;
        const payloadFromState = history.location.state && history.location.state.request
            ? history.location.state
            : null;

        const payload = payloadFromState || readOrderInitPayload();

        if (!payload || !payload.request || !payload.email) {
            this.handleMissingPayload();
            return;
        }

        if (payloadFromState) {
            storeOrderInitPayload(payloadFromState);
        }

        this.initPaymentFromPayload(payload);
    };

    private initPaymentFromPayload = (payload: any) => {
        const { request, email } = payload;
        const points = payload.points ?? 0;
        const codeDiscountAmount = payload.codeDiscountAmount ?? 0;
        const codeDiscountCode = payload.codeDiscountCode || undefined;
        const clientId = payload.clientId ?? 'undefined';

        if (!request?.Bookings || request.Bookings.length === 0) {
            this.handleMissingPayload();
            return;
        }

        let bookings = request.Bookings;
        const schoolId = bookings[0].SchoolId;
        const departmentId = bookings[0].DepartmentId;
        bookings = bookings.map((booking: any) => ({
            lessonId: booking.lessonId,
            levelId: booking.levelId,
            fromDate: booking.From,
            toDate: booking.To,
            meetingPointId: booking.MeetingPointId,
            nbDays: booking.NbDays,
            nb_minutes: booking.NbMinutes,
            nbParticipants: booking.NbParticipants,
            participants: booking.Participants,
            options: booking.Options,
            answers: booking.Answers,
            remark: typeof booking.Remark === 'string' ? booking.Remark.slice(0, REMARK_MAX_LENGTH) : booking.Remark,
            instructorId: booking.InstructorId,
            instructorPriority: booking.InstructorPriority,
        }));

        localStorage?.setItem('pointsUsed', points);
        localStorage?.setItem('codeDiscountAmount', codeDiscountAmount);
        localStorage?.setItem('codeDiscount', codeDiscountCode);
        localStorage?.setItem('clientId', clientId || 'undefined');

        this.getPaymentUrl(
            email,
            bookings,
            schoolId,
            departmentId,
            request.Client,
            points,
            codeDiscountCode,
            request.CodeDiscount,
            request.couponCode,
            request.couponAmount,
        );
    };

    // ------------------------------------------------------------------
    // Actions utilisateur
    // ------------------------------------------------------------------

    handleRetry = () => {
        if (this.paymentArgs.length === 0) return;
        // Reset de l'état d'erreur
        this.setState({
            error: false,
            errorMessage: '',
            errorDetail: '',
            snackbarOpen: false,
            canRetry: false,
            showBackToCart: false,
        });
        const [
            email,
            bookings,
            schoolId,
            departmentId,
            client,
            points,
            codeDiscountCode,
            codeDiscountAmount,
            couponCode,
            couponAmount,
        ] = this.paymentArgs;
        this.getPaymentUrl(
            email,
            bookings,
            schoolId,
            departmentId,
            client,
            points,
            codeDiscountCode,
            codeDiscountAmount,
            couponCode,
            couponAmount,
        );
    };

    handleBackToCart = () => {
        const { history, match } = this.props;
        history.push(`/${match.params.lang}/cart`);
    };

    // ------------------------------------------------------------------
    // Rendu
    // ------------------------------------------------------------------

    render() {
        const { intl } = this.props;
        const {
            error,
            errorMessage,
            errorDetail,
            snackbarOpen,
            canRetry,
            showBackToCart,
        } = this.state;

        return (
            <Container headerBase={intl.formatMessage({ id: 'order.order' })}>
                <div className="__order-parent">
                    {error ? (
                        <>
                            <p className="__order-text">{errorMessage}</p>
                            {errorDetail && (
                                <p className="__order-text" style={{ fontSize: '0.9em', opacity: 0.8 }}>
                                    {errorDetail}
                                </p>
                            )}
                            <div
                                style={{
                                    display: 'flex',
                                    gap: '12px',
                                    marginTop: '16px',
                                    justifyContent: 'center',
                                }}
                            >
                                {canRetry && (
                                    <Button
                                        buttonClasses="__order-button"
                                        onClick={this.handleRetry}
                                    >
                                        {intl.formatMessage({ id: 'order.error.retryButton' })}
                                    </Button>
                                )}
                                {showBackToCart && (
                                    <Button
                                        buttonClasses="__order-button"
                                        onClick={this.handleBackToCart}
                                    >
                                        {intl.formatMessage({ id: 'booking.goToCart' })}
                                    </Button>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <p className="__order-text">{intl.formatMessage({ id: 'order.init' })}</p>
                            <LoadingCircle />
                        </>
                    )}
                </div>
                <Snackbar
                    variant="error"
                    open={snackbarOpen}
                    onClose={() => this.setState({ snackbarOpen: false })}
                    timer={8000}
                    message={errorMessage}
                />
            </Container>
        );
    }
}

export default injectIntl(OrderInit);
